import { create } from "zustand";
import { persist } from "zustand/middleware";
import { directorService } from "@/services/directorService";
import {
  Director,
  CreateDirectorDto,
  UpdateDirectorDto,
  GetPaginationDirectorsDto,
  ExternalDirector,
} from "@/types/director";
import { showToast } from "@/lib/toast";

interface DirectorState {
  // State
  directors: Director[];
  externalDirectors: ExternalDirector[];
  selectedDirector: Director | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  isLoadingDetail: boolean;
  isLoadingExternal: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isSyncing: boolean;
  error: string | null;

  // Actions
  fetchDirectors: (params?: GetPaginationDirectorsDto) => Promise<void>;
  fetchDirectorById: (id: string) => Promise<void>;
  fetchExternalDirectors: () => Promise<void>;
  createDirector: (data: CreateDirectorDto) => Promise<void>;
  createDirectorWithImage: (
    data: CreateDirectorDto,
    imageFile?: File
  ) => Promise<boolean>;
  updateDirector: (id: string, data: UpdateDirectorDto) => Promise<void>;
  updateDirectorWithImage: (
    id: string,
    data: UpdateDirectorDto,
    imageFile?: File
  ) => Promise<boolean>;
  deleteDirector: (id: string) => Promise<void>;
  syncBulk: () => Promise<void>;
  syncSingle: (externalId: string) => Promise<void>;
  setSelectedDirector: (director: Director | null) => void;
  clearSelectedDirector: () => void;
  clearError: () => void;
  setCurrentPage: (page: number) => void;
}

export const useDirectorStore = create<DirectorState>()(
  persist(
    (set, get) => ({
      // Initial state
      directors: [],
      externalDirectors: [],
      selectedDirector: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      isLoading: false,
      isLoadingDetail: false,
      isLoadingExternal: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isSyncing: false,
      error: null,

      // Fetch directors with pagination
      fetchDirectors: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response: any =
            await directorService.getPaginationDirectors(params);
          if (response.ok && response.data) {
            set({
              directors: response.data?.data?.data.directors,
              pagination: response.data?.data?.data.pagination,
              isLoading: false,
            });
          } else {
            set({
              error: response.message || "Lỗi khi tải danh sách đạo diễn",
              isLoading: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải danh sách đạo diễn",
            isLoading: false,
          });
        }
      },

      // Fetch director by ID
      fetchDirectorById: async (id: string) => {
        set({ isLoadingDetail: true, error: null });
        try {
          const response: any = await directorService.getDirectorById(id);
          if (response.ok && response.data) {
            set({
              selectedDirector: response.data?.data?.data,
              isLoadingDetail: false,
            });
          } else {
            set({
              error: response.message || "Lỗi khi tải đạo diễn",
              isLoadingDetail: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải đạo diễn",
            isLoadingDetail: false,
          });
        }
      },

      // Fetch external directors
      fetchExternalDirectors: async () => {
        set({ isLoadingExternal: true, error: null });
        try {
          const response: any = await directorService.getExternalDirectors();
          if (response.ok && response.data) {
            set({
              externalDirectors: response.data?.data?.data,
              isLoadingExternal: false,
            });
          } else {
            set({
              error:
                response.message ||
                "Lỗi khi tải danh sách đạo diễn từ external DB",
              isLoadingExternal: false,
            });
          }
        } catch (error: any) {
          set({
            error:
              error.message || "Lỗi khi tải danh sách đạo diễn từ external DB",
            isLoadingExternal: false,
          });
        }
      },

      // Create director
      createDirector: async (data: CreateDirectorDto) => {
        set({ isCreating: true, error: null });
        try {
          const response: any = await directorService.create(data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchDirectors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isCreating: false });
            showToast.success("Tạo đạo diễn thành công");
          } else {
            set({
              error: response.message || "Lỗi khi tạo đạo diễn",
              isCreating: false,
            });
            showToast.error(response.message || "Lỗi khi tạo đạo diễn");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tạo đạo diễn",
            isCreating: false,
          });
          showToast.error(error.message || "Lỗi khi tạo đạo diễn");
        }
      },

      // Create director with image upload
      createDirectorWithImage: async (
        data: CreateDirectorDto,
        imageFile?: File
      ): Promise<boolean> => {
        set({ isCreating: true, error: null });
        try {
          const response: any = await directorService.createWithImage(
            data,
            imageFile
          );
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchDirectors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isCreating: false });
            showToast.success("Tạo đạo diễn thành công");
            return true;
          } else {
            set({
              error: response.message || "Lỗi khi tạo đạo diễn",
              isCreating: false,
            });
            showToast.error(
              response.data?.message || response.message || "Lỗi khi tạo đạo diễn"
            );
            return false;
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tạo đạo diễn",
            isCreating: false,
          });
          showToast.error(error.message || "Lỗi khi tạo đạo diễn");
          return false;
        }
      },

      // Update director
      updateDirector: async (id: string, data: UpdateDirectorDto) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await directorService.updateDirector(id, data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchDirectors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isUpdating: false });
            showToast.success("Cập nhật đạo diễn thành công");
          } else {
            set({
              error: response.message || "Lỗi khi cập nhật đạo diễn",
              isUpdating: false,
            });
            showToast.error(response.message || "Lỗi khi cập nhật đạo diễn");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi cập nhật đạo diễn",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi cập nhật đạo diễn");
        }
      },

      // Update director with image upload
      updateDirectorWithImage: async (
        id: string,
        data: UpdateDirectorDto,
        imageFile?: File
      ): Promise<boolean> => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await directorService.updateWithImage(
            id,
            data,
            imageFile
          );
          if (response.ok && response.data) {
            set({
              isUpdating: false,
              selectedDirector: response.data?.data?.data,
            });
            showToast.success("Cập nhật đạo diễn thành công");
            return true;
          } else {
            set({
              error: response.message || "Lỗi khi cập nhật đạo diễn",
              isUpdating: false,
            });
            showToast.error(
              response.data?.message ||
                response.message ||
                "Lỗi khi cập nhật đạo diễn"
            );
            return false;
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi cập nhật đạo diễn",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi cập nhật đạo diễn");
          return false;
        }
      },

      // Delete director
      deleteDirector: async (id: string) => {
        set({ isDeleting: true, error: null });
        try {
          const response: any = await directorService.deleteDirector(id);
          if (response.ok) {
            const { pagination } = get();
            await get().fetchDirectors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isDeleting: false, selectedDirector: null });
            showToast.success("Xóa đạo diễn thành công");
          } else {
            set({
              error: response.message || "Lỗi khi xóa đạo diễn",
              isDeleting: false,
            });
            showToast.error(response.message || "Lỗi khi xóa đạo diễn");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi xóa đạo diễn",
            isDeleting: false,
          });
          showToast.error(error.message || "Lỗi khi xóa đạo diễn");
        }
      },

      // Sync bulk from external DB
      syncBulk: async () => {
        set({ isSyncing: true, error: null });
        try {
          const response: any = await directorService.syncBulk();
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchDirectors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isSyncing: false });
            const data = response.data?.data?.data;
            showToast.success(
              `Sync thành công: ${data?.created} tạo mới, ${data?.updated} cập nhật`
            );
          } else {
            set({
              error: response.message || "Lỗi khi sync đạo diễn",
              isSyncing: false,
            });
            showToast.error(response.message || "Lỗi khi sync đạo diễn");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi sync đạo diễn",
            isSyncing: false,
          });
          showToast.error(error.message || "Lỗi khi sync đạo diễn");
        }
      },

      // Sync single from external DB
      syncSingle: async (externalId: string) => {
        set({ isSyncing: true, error: null });
        try {
          const response: any = await directorService.syncSingle(externalId);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchDirectors({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isSyncing: false });
            showToast.success("Sync đạo diễn thành công");
          } else {
            set({
              error: response.message || "Lỗi khi sync đạo diễn",
              isSyncing: false,
            });
            showToast.error(response.message || "Lỗi khi sync đạo diễn");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi sync đạo diễn",
            isSyncing: false,
          });
          showToast.error(error.message || "Lỗi khi sync đạo diễn");
        }
      },

      // Utilities
      setSelectedDirector: (director: Director | null) =>
        set({ selectedDirector: director }),
      clearSelectedDirector: () => set({ selectedDirector: null }),
      clearError: () => set({ error: null }),
      setCurrentPage: (page: number) =>
        set((state) => ({
          pagination: { ...state.pagination, page },
        })),
    }),
    {
      name: "director-storage",
      partialize: (state) => ({ pagination: state.pagination }),
    }
  )
);
